import { withRouter } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import { useEffect, useState } from "react";
import {
    Button,
    Row,
    Col,
    Label,
    Spinner,
    UncontrolledTooltip,
    Alert as ReactstrapAlert
} from "reactstrap";
import Alert from "../../../alert";
import bottyCustomizeIcon from '../../../../../../../../assets/images/ai-onboarding/botty-customize.svg';
import FileUploader from "../../../file-uploader";
import axios from "axios";
import {
    AI_ONBOARDING_BASE_URL
} from '../../../../../../../../constants'
import { toast } from "react-toastify";
import { useMutation, useQuery } from "@tanstack/react-query";
import AIOnboardingService from "src/modules/3rd-party-management/apis/admin/AIOnboardingService";

const UploadFiles = ({
    t,
    token,
    onNextButtonClicked,
    onBackButtonClicked,
    onCancelButtonClicked
}) => {
    const [downloadStatuses, setDownloadStatuses] = useState({
        masterFile: {
            status: null
        },
        creditorRevenue: {
            status: null
        },
        legalEntity: {
            status: null
        },
        product: {
            status: null
        }
    })

    const [uploadStatuses, setUploadStatuses] = useState({
        masterFile: {
            status: false
        },
        creditorRevenue: {
            status: true
        },
        legalEntity: {
            status: true
        },
        product: {
            status: true
        }
    })

    const {
        data: uploadedFile,
        isFetching: fetchingOldFiles
    } = useQuery({
        queryKey: [
            '3rd-party-management-ai-onboarding-latest-uploaded-file'
        ],
        queryFn: async () => {
            const service = AIOnboardingService.getInstance();
            return await service.getCurrentUploadedFile();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching the current status of the operation.'), {
                type: 'error'
            })
        }
    });

    const {
        mutate: startValidateCompleteness,
        isLoading: validateCompletenessInProcess,
        data: validateCompletenessData
    } = useMutation({
        mutationFn: async () => {
            const service = AIOnboardingService.getInstance();
            return await service.validateCompleteness();
        },
        onError: () => {
            toast(t("Unable to start the validation process. Please try again."), {
                type: "error"
            });
        }
    });

    const handleDownloadTemplate = (template) => {
        setDownloadStatuses((currentStatus) => {
            return {
                ...currentStatus, [template]: {
                    ...currentStatus[template],
                    status: 'downloading'
                }
            }
        })

        axios.get(`${AI_ONBOARDING_BASE_URL}/onboarding/template/${template}/download`,
            {
                responseType: "blob",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                onDownloadProgress: (e) => {
                    let percentCompleted = Math.round(e.loaded * 100 /
                        e.total);

                    if (percentCompleted >= 100) {
                        setDownloadStatuses((currentStatus) => {
                            return {
                                ...currentStatus, [template]: {
                                    ...currentStatus[template],
                                    status: 'completed'
                                }
                            }
                        })
                    }
                }
            }
        ).then((e) => {
            const url = window.URL.createObjectURL(
                new Blob([e.data], { type: "*" })
            );

            const link = document.createElement("a");

            link.href = url;
            link.setAttribute("download", `${template}.xlsx`);

            document.body.appendChild(link);

            link.click();
            link.remove();
        }).catch(() => {
            toast(t("An error occurred while downloading the file."), {
                type: "error",
            });

            setDownloadStatuses((currentStatus) => {
                return {
                    ...currentStatus, [template]: {
                        ...currentStatus[template],
                        status: 'failed'
                    }
                }
            })
        })
    }

    const handleFileUploadCallback = (fileType) => {
        setUploadStatuses((currentStatus) => {
            return {
                ...currentStatus,
                [fileType]: {
                    status: true
                }
            }
        })
    }

    useEffect(() => {
        if (uploadedFile?.id) {
            setUploadStatuses((currentStatus) => {
                return {
                    ...currentStatus,
                    'masterFile': {
                        status: true
                    }
                }
            })
        }
    }, [uploadedFile])

    useEffect(() => {
        if (validateCompletenessData) {
            if (validateCompletenessData.status === 'submitted') {
                onNextButtonClicked();
            }
            else {
                toast(t("Unable to start the validation process. Please try again."), {
                    type: "error"
                });
            }
        }
        
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        validateCompletenessData,
        t
    ]);

    const allRequiredFilesUploaded = Object.keys(uploadStatuses).every((fileType) => {
        return uploadStatuses[fileType].status
    })

    return (
        <>
            <Alert
                icon={bottyCustomizeIcon}
                title='Upload files'
                message={t('Please upload your file or set an interface to your system in the settings.')}
            />

            <div className="ai-onboarding-content-body">
                {validateCompletenessData && validateCompletenessData.status !== 'submitted' && (
                    <ReactstrapAlert
                        color="danger"
                        className="mb-4"
                    >
                        <h6 className="alert-heading mb-2">
                            {t('Unable to Start Validation')}
                        </h6>
                        <p className="mb-0">
                            {t('An error occurred while initiating the AI validation process. Please try again. If the issue persists, contact support.')}
                        </p>
                    </ReactstrapAlert>
                )}

                <div className="upload-files-items">
                    <Row className="mb-4">
                        <Col sm='12'>
                            <Label className="form-label">
                                <span className="me-1">
                                    {`1. ${t('Creditor/Debtor Balance Sheet')}`}
                                </span>
                                <i
                                    className="ri-information-line"
                                    id="upload-file-helper"
                                />
                                <UncontrolledTooltip
                                    target="upload-file-helper"
                                    trigger="hover"
                                >
                                    {t('You will find the balance sheet with relevant information in your accounting system. Get an export from your accounting department.')}
                                </UncontrolledTooltip>
                            </Label>
                        </Col>
                        {fetchingOldFiles ? (
                            <>
                                <Col sm='12' md='9'>
                                    <div style={{ width: '100%', height: '60px' }} className="dt-field dt-skeleton mb-2" />
                                    <div style={{ width: '100%', height: '60px' }} className="dt-field dt-skeleton" />
                                </Col>
                                <Col sm='12' md='3' className="d-flex align-items-center">
                                    <div style={{ width: '100%', height: '60px' }} className="dt-field dt-skeleton" />
                                </Col>
                            </>
                        ) : (
                            <>
                                <Col sm='12' md='9'>
                                    <FileUploader
                                        className='w-100'
                                        fileType='masterFile'
                                        onNewFileUploaded={handleFileUploadCallback}
                                        prevFiles={uploadedFile?.id ? [
                                            {
                                                id: uploadedFile.id,
                                                name: uploadedFile.masterFile,
                                                formattedSize: 1000
                                            }
                                        ] : null}
                                    />
                                </Col>
                                <Col sm='12' md='3' className="d-flex align-items-center">
                                    <Button outline color="primary" onClick={() => {
                                        handleDownloadTemplate('masterFile')
                                    }}>
                                        {downloadStatuses.masterFile.status === 'downloading' ? (
                                            <>
                                                <Spinner color="primary" size='sm' className="me-2" />
                                                {t('Downloading')}...
                                            </>
                                        ) : (
                                            <>
                                                {t('Download Excel Template')}
                                            </>
                                        )}
                                    </Button>
                                </Col>
                            </>
                        )}
                    </Row>

                    <Row className="mb-4">
                        <Col sm='12'>
                            <Label className="form-label">
                                {t('2. Product List')}
                            </Label>
                        </Col>
                        <Col sm='12' md='9'>
                            <FileUploader
                                className='w-100'
                                fileType='product'
                                onNewFileUploaded={handleFileUploadCallback}
                            />
                        </Col>
                        <Col sm='12' md='3' className="d-flex align-items-center">
                            <Button outline color="primary" onClick={() => {
                                handleDownloadTemplate('product')
                            }}>
                                {downloadStatuses.product.status === 'downloading' ? (
                                    <>
                                        <Spinner color="primary" size='sm' className="me-2" />
                                        {t('Downloading')}...
                                    </>
                                ) : (
                                    <>
                                        {t('Download Excel Template')}
                                    </>
                                )}
                            </Button>
                        </Col>
                    </Row>

                    {/* <Row className="mb-4">
                        <Col sm='12'>
                            <Label className="form-label">
                                {t('2. Creditor Revenue')}
                            </Label>
                        </Col>
                        <Col sm='12' md='9'>
                            <FileUploader 
                                className='w-100'
                                fileType='creditorRevenue'
                                onNewFileUploaded={handleFileUploadCallback}
                            />
                        </Col>
                        <Col sm='12' md='3' className="d-flex align-items-center">
                            <Button outline color="primary" onClick={() => {
                                handleDownloadTemplate('creditorRevenue')
                            }}>
                                {downloadStatuses.creditorRevenue.status === 'downloading' ? (
                                    <>
                                        <Spinner color="primary" size='sm' className="me-2"/>
                                        {t('Downloading')}...
                                    </>
                                ) : (
                                    <>
                                        {t('Download Excel Template')}
                                    </>
                                )}
                            </Button>
                        </Col>
                    </Row>

                    <Row className="mb-4">
                        <Col sm='12'>
                            <Label className="form-label">
                                {t('3. Legal Entity List')}
                            </Label>
                        </Col>
                        <Col sm='12' md='9'>
                            <FileUploader 
                                className='w-100'
                                fileType='legalEntity'
                                onNewFileUploaded={handleFileUploadCallback}
                            />
                        </Col>
                        <Col sm='12' md='3' className="d-flex align-items-center">
                            <Button outline color="primary" onClick={() => {
                                handleDownloadTemplate('legalEntity')
                            }}>
                                {downloadStatuses.legalEntity.status === 'downloading' ? (
                                    <>
                                        <Spinner color="primary" size='sm' className="me-2"/>
                                        {t('Downloading')}...
                                    </>
                                ) : (
                                    <>
                                        {t('Download Excel Template')}
                                    </>
                                )}
                            </Button>
                        </Col>
                    </Row>*/}
                </div>

                <div className="ai-onboarding-tab-actions">
                    <div
                        className="d-flex justify-content-center align-items-center gap-2"
                    >
                        <Button
                            outline
                            color="secondary"
                            onClick={onBackButtonClicked}>
                            {t('Back')}
                        </Button>

                        <Button
                            color="primary"
                            onClick={startValidateCompleteness}
                            disabled={validateCompletenessInProcess}
                        // disabled={!allRequiredFilesUploaded || fetchingOldFiles}
                        >
                            {validateCompletenessInProcess && (
                                <Spinner 
                                    className="me-1"
                                    size="sm"
                                />
                            )}
                            {t('Next')}
                        </Button>
                    </div>

                    <Button
                        color="secondary"
                        outline
                        onClick={onCancelButtonClicked}
                    >
                        {t('Cancel')}
                    </Button>
                </div>
            </div>
        </>
    )
}

const mapStatetoProps = (state) => {
    const { token } = state.Login;
    return {
        user: state.Login.user,
        token: token
    };
};

export default withNamespaces()(
    withRouter(connect(
        mapStatetoProps,
        {}
    )(UploadFiles))
)